import { useState } from "react";
import { openPdf } from "@/lib/print";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const TABS = [
  { key: "trial_balance", label: "Trial Balance" },
  { key: "profit_and_loss", label: "Profit & Loss" },
  { key: "balance_sheet", label: "Balance Sheet" },
  { key: "cash_bank_summary", label: "Cash & Bank" },
  { key: "cash_flow", label: "Cash Flow" },
];
const RANGE_TABS = ["profit_and_loss", "cash_flow"];

/** Insert group headings (with subtotals) above the accounts that belong to them. Top-level groups are skipped. */
function withGroups(rows, accounts, keys) {
  if (!accounts) return rows.map((r) => ({ ...r, level: 0 }));
  const byCode = Object.fromEntries(accounts.map((a) => [a.code, a]));
  const byId = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const chainOf = (acc) => {
    const out = [];
    let p = acc?.parent_id ? byId[acc.parent_id] : null;
    while (p) { if (p.parent_id) out.unshift(p); p = p.parent_id ? byId[p.parent_id] : null; }
    return out;
  };
  const totals = {};
  const withChain = rows.map((r) => ({ r, chain: chainOf(byCode[r.account_code]) }));
  withChain.forEach(({ r, chain }) => chain.forEach((g) => {
    if (!totals[g.id]) totals[g.id] = Object.fromEntries(keys.map((k) => [k, 0]));
    keys.forEach((k) => { totals[g.id][k] += Number(r[k] || 0); });
  }));
  const path = ({ r, chain }) => [...chain.map((g) => g.code), r.account_code].join("/");
  withChain.sort((a, b) => path(a).localeCompare(path(b), undefined, { numeric: true }));
  const out = [];
  const shown = new Set();
  withChain.forEach(({ r, chain }) => {
    chain.forEach((g, i) => {
      if (shown.has(g.id)) return;
      shown.add(g.id);
      out.push({ account_code: g.code, account_name: g.name, is_group: true, level: i, ...totals[g.id] });
    });
    out.push({ ...r, level: chain.length });
  });
  return out;
}

export default function FinancialStatements() {
  const { companies } = useCompany() || {};
  const [tab, setTab] = useState("trial_balance");
  const [companyId, setCompanyId] = useState(companies?.[0]?.id || "");
  const [asOf, setAsOf] = useState(todayISO());
  const [dateFrom, setDateFrom] = useState(todayISO().slice(0, 8) + "01");
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [accounts, setAccounts] = useState(null);

  const toggleGrouped = async (v) => {
    setGrouped(v);
    if (v && !accounts) {
      try { const { data } = await api.get("/gl/accounts"); setAccounts(data); } catch (err) { toast.error(getErrorMessage(err)); }
    }
  };
  const groupAccounts = grouped ? accounts : null;

  const effectiveCompanyId = companyId || companies?.[0]?.id;

  const load = async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    try {
      const endpoint = { trial_balance: "trial-balance", profit_and_loss: "profit-and-loss", balance_sheet: "balance-sheet", cash_bank_summary: "cash-bank-summary", cash_flow: "cash-flow" }[tab];
      const params = RANGE_TABS.includes(tab) ? { company_id: effectiveCompanyId, date_from: dateFrom, date_to: dateTo } : { company_id: effectiveCompanyId, as_of: asOf };
      const { data } = await api.get(`/reports/${endpoint}`, { params });
      setData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    setExporting(true);
    try {
      const response = await api.post(
        "/reports/export",
        { report_key: tab === "cash_bank_summary" ? "cash_bank_summary" : tab, format, company_id: effectiveCompanyId, as_of: asOf, date_from: dateFrom, date_to: dateTo },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="financial-statements-page">
      <PageHeader
        title="Financial Statements"
        description="Trial Balance, Profit & Loss, Balance Sheet and Cash & Bank — per company, as of a date."
        action={
          <div className="flex items-center gap-2">
            <Select value={companyId || companies?.[0]?.id || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-48" data-testid="financials-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {RANGE_TABS.includes(tab) ? (
              <>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" data-testid="financials-date-from-input" />
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" data-testid="financials-date-to-input" />
              </>
            ) : (
              <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-36" data-testid="financials-as-of-input" />
            )}
            <Button onClick={load} disabled={loading} data-testid="financials-run-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}</Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <Button key={t.key} size="sm" variant={tab === t.key ? "default" : "outline"} onClick={() => { setTab(t.key); setData(null); }} data-testid={`tab-${t.key}-button`}>
            {t.label}
          </Button>
        ))}
        {["trial_balance", "profit_and_loss", "balance_sheet"].includes(tab) && (
          <label className="ml-2 flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={grouped} onCheckedChange={toggleGrouped} data-testid="financials-grouped-switch" /> Show groups
          </label>
        )}
        {data && tab === "cash_flow" && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => openPdf("/reports/cash-flow/pdf", { company_id: effectiveCompanyId, date_from: dateFrom, date_to: dateTo })} data-testid="cash-flow-pdf-button">
            <Download className="h-3.5 w-3.5" /> PDF
          </Button>
        )}
        {data && !["cash_bank_summary", "cash_flow"].includes(tab) && (
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => exportReport("excel")} data-testid="export-excel-button"><Download className="h-3.5 w-3.5" /> Excel</Button>
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => exportReport("pdf")} data-testid="export-pdf-button"><Download className="h-3.5 w-3.5" /> PDF</Button>
          </div>
        )}
      </div>

      {!data ? (
        <EmptyState title="Run a statement" description="Pick a company and date, then click Run." testId="financials-empty-state" />
      ) : tab === "trial_balance" ? (
        <TrialBalanceView data={data} accounts={groupAccounts} />
      ) : tab === "profit_and_loss" ? (
        <ProfitAndLossView data={data} accounts={groupAccounts} />
      ) : tab === "balance_sheet" ? (
        <BalanceSheetView data={data} accounts={groupAccounts} />
      ) : tab === "cash_flow" ? (
        <CashFlowView data={data} />
      ) : (
        <CashBankView data={data} />
      )}
    </div>
  );
}

function TrialBalanceView({ data, accounts }) {
  const rows = withGroups(data.rows, accounts, ["debit", "credit"]);
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="trial-balance-view">
      {!data.balanced && (
        <div className="border-b border-border bg-rose-500/10 px-4 py-2 text-xs font-medium text-rose-500" data-testid="trial-balance-unbalanced-warning">
          Trial balance does not balance — please investigate before relying on this report.
        </div>
      )}
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.is_group ? "g" : "a"}-${r.account_code}`} className={r.is_group ? "bg-muted/40 font-semibold" : ""}>
              <TableCell className="font-mono text-xs">{r.account_code}</TableCell>
              <TableCell className="text-foreground" style={{ paddingLeft: `${(r.level || 0) * 18 + 16}px` }}>{r.account_name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.account_type}</TableCell>
              <TableCell className="text-right font-mono">{fmtNum(r.debit, 2)}</TableCell>
              <TableCell className="text-right font-mono">{fmtNum(r.credit, 2)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell colSpan={3}>Total</TableCell><TableCell className="text-right font-mono" data-testid="trial-balance-total-debit">{fmtNum(data.total_debit, 2)}</TableCell><TableCell className="text-right font-mono" data-testid="trial-balance-total-credit">{fmtNum(data.total_credit, 2)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function ProfitAndLossView({ data, accounts }) {
  return (
    <div className="space-y-4" data-testid="profit-and-loss-view">
      <Section title="Income" rows={data.income} total={data.total_income} accounts={accounts} />
      <Section title="Expense" rows={data.expense} total={data.total_expense} accounts={accounts} />
      <div className="rounded-xl border border-border bg-card p-4 text-right">
        <span className="text-sm text-muted-foreground">Net Profit: </span>
        <span className="font-mono text-lg font-semibold text-foreground" data-testid="net-profit-value">₹{fmtNum(data.net_profit, 2)}</span>
      </div>
    </div>
  );
}

function BalanceSheetView({ data, accounts }) {
  return (
    <div className="space-y-4" data-testid="balance-sheet-view">
      {!data.balanced && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-medium text-rose-500" data-testid="balance-sheet-unbalanced-warning">
          Balance sheet does not balance — difference ₹{fmtNum(data.difference, 2)}.
        </div>
      )}
      <Section title="Assets" rows={data.assets} total={data.total_assets} accounts={accounts} />
      <Section title="Liabilities" rows={data.liabilities} accounts={accounts} />
      <Section title="Equity" rows={data.equity} accounts={accounts} />
      <div className="rounded-xl border border-border bg-card p-4 flex justify-between text-sm">
        <span className="text-muted-foreground">Total Liabilities + Equity (incl. net income to date)</span>
        <span className="font-mono font-semibold text-foreground" data-testid="balance-sheet-total-liab-equity">₹{fmtNum(data.total_liabilities_and_equity, 2)}</span>
      </div>
    </div>
  );
}

function CashBankView({ data }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="cash-bank-view">
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.accounts.map((r) => (
            <TableRow key={r.account_code}><TableCell className="font-mono text-xs">{r.account_code}</TableCell><TableCell className="text-foreground">{r.account_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.balance, 2)}</TableCell></TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell colSpan={2}>Total</TableCell><TableCell className="text-right font-mono">{fmtNum(data.total, 2)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function Section({ title, rows: rawRows, total, accounts }) {
  const rows = withGroups(rawRows, accounts, ["amount"]);
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">{title}</h3></div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">No entries.</p>
      ) : (
        <Table>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.is_group ? "g" : "a"}-${r.account_code}`} className={r.is_group ? "bg-muted/40 font-semibold" : ""}><TableCell className="font-mono text-xs w-24">{r.account_code}</TableCell><TableCell className="text-foreground" style={{ paddingLeft: `${(r.level || 0) * 18 + 16}px` }}>{r.account_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.amount, 2)}</TableCell></TableRow>
            ))}
            {total !== undefined && (
              <TableRow className="font-semibold"><TableCell colSpan={2}>Total</TableCell><TableCell className="text-right font-mono">{fmtNum(total, 2)}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function CashFlowView({ data }) {
  const money = (v) => `₹${fmtNum(v, 2)}`;
  return (
    <div className="space-y-4" data-testid="cash-flow-view">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["Opening cash", data.opening], ["Net change", data.net_change], ["Closing cash", data.closing]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k}</p>
            <p className={`mt-2 font-mono text-lg font-bold ${k === "Net change" && Number(v) < 0 ? "text-rose-500" : "text-foreground"}`}>{money(v)}</p>
          </div>
        ))}
        <div className="rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
          <p className="font-semibold uppercase tracking-wider">Cash accounts</p>
          <p className="mt-2">{data.cash_accounts.join(", ")}</p>
          <p className={`mt-1 font-medium ${data.check_ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500"}`}>{data.check_ok ? "Opening + change = closing ✓" : "Opening + change ≠ closing — check"}</p>
        </div>
      </div>
      {data.sections.map((sec) => (
        <div key={sec.key} className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <h3 className="text-sm font-semibold text-foreground">{sec.label}</h3>
            <span className={`font-mono text-sm font-semibold ${Number(sec.total) < 0 ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"}`}>{money(sec.total)}</span>
          </div>
          {sec.rows.length === 0 ? <p className="px-4 py-3 text-xs text-muted-foreground">No movement in this period.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Line</TableHead><TableHead className="text-right">Money in</TableHead><TableHead className="text-right">Money out</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
              <TableBody>
                {sec.rows.map((r) => (
                  <TableRow key={`${r.account_code}-${r.label}`}>
                    <TableCell className="text-foreground">{r.label} <span className="font-mono text-[10px] text-muted-foreground">{r.account_code}</span></TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(r.inflow) ? fmtNum(r.inflow, 2) : ""}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(r.outflow) ? fmtNum(r.outflow, 2) : ""}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold">{fmtNum(r.net, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ))}
    </div>
  );
}
